import { useCallback, useState } from 'react';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import { IReuseOrderModel, ReuseResult } from 'types';
import InfoIcon from 'assets/info.svg?react';
import { themeColors } from 'app/shared/theme';
import { useStyles } from './ReuseModelsItem.styles';
import { useBatchHistoryList } from 'app/shared/hooks/useBatchHistoryList';

import { useTranslation } from 'react-i18next';
import { useAuthClient } from 'app/shared/hooks/useAuthClient';
import { DosageTableModal, IDosageTableData } from './DosageTableModal';

interface IMeasurementLinkIconProps {
  link?: {
    key?: string;
    value?: string;
  };
}

export const MeasurementLinkIcon = ({ link }: IMeasurementLinkIconProps) => {
  const { classes } = useStyles();
  const authClient = useAuthClient();
  const { organizationId }: { organizationId: string } = useRouteParams();
  const [dosageTable, setDosageTable] = useState<IDosageTableData>();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (!link?.value) return;

      try {
        const response = await authClient(
          `/api/v1/${organizationId}${link.value}`,
          { method: 'GET' },
        );
        setDosageTable(response?.data ?? response);
      } catch (error) {
        console.error('Unable to load dosage table', error);
      }
    },
    [authClient, link?.value, organizationId],
  );

  if (!link?.value) return null;

  return (
    <>
      <div
        className={classes.infoIcon}
        onClick={handleClick}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        title={link.key || 'Open dosage table'}
        role="button"
        tabIndex={0}
      >
        <InfoIcon
          height={16}
          width={16}
          stroke={isHovered ? themeColors.mbsBlue : themeColors.greyscale2}
        />
      </div>
      <DosageTableModal
        data={dosageTable}
        onClose={() => setDosageTable(undefined)}
      />
    </>
  );
};

interface IReuseModelsItemProps {
  reuseOrderModels: IReuseOrderModel[];
  setInfo: (info: IReuseOrderModel) => void;
  refresh: () => void;
  openModal: (
    type: ReuseResult,
    message: string,
    reuseOrderModel: IReuseOrderModel,
  ) => void;
}

interface IReuseModelInfoProps {
  reuseOrderModel: IReuseOrderModel;
  setInfo: (info: IReuseOrderModel) => void;
  refresh: () => void;
  openModal: (
    type: ReuseResult,
    message: string,
    reuseOrderModel: IReuseOrderModel,
  ) => void;
}

const ReuseModelInfo = ({
  reuseOrderModel,
  setInfo,
  refresh,
  openModal,
}: IReuseModelInfoProps) => {
  const { organizationId }: { organizationId: string } = useRouteParams();
  const { createReuseConcretePost } = useBatchHistoryList({
    organizationId,
  });
  const { t } = useTranslation();
  const [isInfoHovered, setIsInfoHovered] = useState(false);

  const handleReuseConcrete = useCallback(
    async (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        const res = await createReuseConcretePost({
          returnConcreteOrderDataId: reuseOrderModel.returnConcreteOrderDataId,
          partitionDateUtc: reuseOrderModel.partitionDateUtc,
        });
        if (res.data.reuseResult === ReuseResult.CLOSED) {
          refresh();
        } else {
          openModal(
            res.data.reuseResult,
            res.data.reuseResultText,
            reuseOrderModel,
          );
        }
      } catch (error) {
        console.log(error);
      }
    },
    [createReuseConcretePost, refresh, openModal, reuseOrderModel],
  );

  const { classes } = useStyles();

  const handleInfoClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setInfo(reuseOrderModel);
    },
    [reuseOrderModel, setInfo],
  );

  const handleInfoEnter = useCallback(() => {
    setIsInfoHovered(true);
  }, []);

  const handleInfoLeave = useCallback(() => {
    setIsInfoHovered(false);
  }, []);

  return (
    <div className={classes.linkContainer}>
      <span
        className={reuseOrderModel.chosenForReuse ? undefined : classes.link}
        onClick={
          !reuseOrderModel.chosenForReuse ? handleReuseConcrete : undefined
        }
      >
        {`${t('reuseModelsItem.dispatchOrder')} ${
          reuseOrderModel.dispatchOrderId
        }`}
      </span>
      <div
        className={classes.infoIcon}
        onClick={handleInfoClick}
        onPointerEnter={handleInfoEnter}
        onPointerLeave={handleInfoLeave}
      >
        <InfoIcon
          height={16}
          width={16}
          stroke={isInfoHovered ? themeColors.mbsBlue : themeColors.greyscale2}
        />
      </div>
    </div>
  );
};

export const ReuseModelsItem = ({
  reuseOrderModels,
  setInfo,
  refresh,
  openModal,
}: IReuseModelsItemProps) => {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      {reuseOrderModels.map(el => (
        <ReuseModelInfo
          key={el.returnConcreteOrderDataId}
          reuseOrderModel={el}
          setInfo={setInfo}
          refresh={refresh}
          openModal={openModal}
        />
      ))}
    </div>
  );
};
